import {
  Citizen,
  Report,
  ReportUpdate,
  ReportStatus,
  Tag,
  CitizenRequest,
  CitizenCategory,
  Contact,
  Country,
  DashboardStats,
  FeaturedSection,
  FeaturedCitizen,
  Gender,
} from "@/types";
import { supabase } from "./supabase";
import { debounce } from "lodash";

const API_URL = import.meta.env.VITE_API_URL;

export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  if (!API_URL) {
    throw new Error("API_URL is not configured");
  }

  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    throw new Error("Failed to get session");
  }

  if (!session?.access_token) {
    throw new Error("No valid session found");
  }

  const defaultHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };

  const fullUrl = `${API_URL}${url}`;
  const requestOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    signal: options.signal,
  };

  try {
    const response = await fetch(fullUrl, requestOptions);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name !== "AbortError") {
      throw error;
    }
    throw error;
  }
}

// Add this interface for the create response
interface CreateCitizenResponse {
  citizen: Citizen;
}

// Add the type for the response
interface ValidationResponse {
  results: {
    citizen_name: string;
    citizen_slug: string;
    exists: boolean;
    existing_id?: number;
    name_exists?: boolean;
    slug_exists?: boolean;
  }[];
  total: number;
  existing_count: number;
}

// Move the interface outside of the CitizensAPI object
interface ImportResponse {
  message: string;
  failures?: {
    error: string;
    index: number;
    citizen: string | null;
  }[];
  failure_count: number;
  success_count: number;
}

// Citizens API
export const CitizensAPI = {
  getAll: async (
    page = 1,
    limit = 10,
    query = "",
    signal?: AbortSignal,
    orderBy?: string,
    isVerified?: string | null,
    isActive?: string | null
  ): Promise<{
    data: Citizen[];
    pagination: {
      total: number;
      offset: number;
      limit: number;
    };
  }> => {
    // Build the query parameters
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (query) {
      params.append("searchTerm", encodeURIComponent(query));
    }

    if (orderBy) {
      params.append("order_by", orderBy);
    }

    if (isVerified !== null && isVerified !== undefined) {
      params.append("is_verified", isVerified);
    }

    if (isActive !== null && isActive !== undefined) {
      params.append("is_active", isActive);
    }

    const response = await fetchWithAuth(
      `/admin/citizens?${params.toString()}`,
      { signal }
    );

    // Handle both array and object responses
    if (Array.isArray(response)) {
      return {
        data: response,
        pagination: {
          total: response.length,
          offset: 0,
          limit: limit,
        },
      };
    }

    // If the API doesn't return the new pagination format yet, adapt the old format
    if (response.data && response.total !== undefined && !response.pagination) {
      return {
        data: response.data,
        pagination: {
          total: response.total,
          offset: (page - 1) * limit,
          limit: limit,
        },
      };
    }

    return response;
  },

  getById: async (id: number): Promise<Citizen> => {
    const controller = new AbortController();
    return fetchWithAuth(`/citizens/id/${id}`, {
      signal: controller.signal,
    });
  },

  create: async (citizen: Partial<Citizen>): Promise<CreateCitizenResponse> => {
    return fetchWithAuth("/citizens", {
      method: "POST",
      body: JSON.stringify(citizen),
    });
  },

  update: async (id: number, citizen: Partial<Citizen>): Promise<Citizen> => {
    return fetchWithAuth(`/citizens/${id}`, {
      method: "PUT",
      body: JSON.stringify(citizen),
    });
  },

  delete: async (id: number): Promise<void> => {
    return fetchWithAuth(`/citizens/${id}`, {
      method: "DELETE",
    });
  },

  getPendingRequests: async (
    page = 1,
    limit = 10,
    categoryId?: number
  ): Promise<{ data: CitizenRequest[]; total: number }> => {
    const categoryParam = categoryId ? `&categoryId=${categoryId}` : "";
    const response = await fetchWithAuth(
      `/citizen-requests?page=${page}&limit=${limit}${categoryParam}`
    );
    // If response is an array, wrap it in the expected format
    if (Array.isArray(response)) {
      return {
        data: response,
        total: response.length,
      };
    }
    return response;
  },

  getCategories: async (): Promise<CitizenCategory[]> => {
    return fetchWithAuth("/citizen-categories");
  },

  updateRequestStatus: async (
    id: number,
    status: "approved" | "rejected" | "pending" | "duplicate",
    adminNotes?: string
  ): Promise<void> => {
    return fetchWithAuth(`/citizen-requests/${id}`, {
      method: "PUT",
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  },

  validateBulk: async (
    citizens: { citizen_name: string; citizen_slug: string }[]
  ): Promise<ValidationResponse> => {
    return fetchWithAuth("/citizens/validate", {
      method: "POST",
      body: JSON.stringify({ citizens }),
    });
  },

  bulkImport: async (citizens: any[]): Promise<ImportResponse> => {
    return fetchWithAuth("/citizens/bulk-import", {
      method: "POST",
      body: JSON.stringify({ citizens }),
    });
  },
};

// Reports API
export const ReportsAPI = {
  getAll: async (
    page = 1,
    limit = 10,
    status?: ReportStatus
  ): Promise<{ data: Report[]; total: number }> => {
    const statusParam = status ? `&status=${status}` : "";
    return fetchWithAuth(`/reports?page=${page}&limit=${limit}${statusParam}`);
  },

  getById: async (id: number): Promise<Report> => {
    return fetchWithAuth(`/reports/${id}`);
  },

  update: async (id: number, update: ReportUpdate): Promise<Report> => {
    return fetchWithAuth(`/reports/${id}`, {
      method: "PUT",
      body: JSON.stringify(update),
    });
  },

  resolve: async (id: number, adminNote?: string): Promise<Report> => {
    return fetchWithAuth(`/reports/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ admin_note: adminNote }),
    });
  },

  reject: async (id: number, adminNote?: string): Promise<Report> => {
    return fetchWithAuth(`/reports/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ admin_note: adminNote }),
    });
  },
};

// Dashboard Stats API
export const StatsAPI = {
  getDashboardStats: async (): Promise<{
    totalCitizens: number;
    pendingCitizens: number;
    pendingReports: number;
    totalReports: number;
    recentActivity: {
      type: string;
      description: string;
      timestamp: string;
    }[];
  }> => {
    return fetchWithAuth("/stats/dashboard");
  },
};

// Single, stable debounced function
const debouncedTagSearch = debounce(async (query: string): Promise<Tag[]> => {
  return fetchWithAuth(`/tags/search?q=${encodeURIComponent(query)}`);
}, 300);

export const TagsAPI = {
  search: async (query: string): Promise<Tag[]> => {
    if (!query || query.length < 3) return [];
    try {
      const results = await debouncedTagSearch(query);
      return results;
    } catch (error) {
      return [];
    }
  },
};

export const ContactsAPI = {
  getAll: async (
    page = 1,
    limit = 30,
    subject?: string,
    name?: string
  ): Promise<{ data: Contact[]; count: number }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (subject) {
      params.append("subject", subject);
    }

    if (name) {
      params.append("name", name);
    }

    return fetchWithAuth(`/contact?${params.toString()}`);
  },

  update: async (id: number, updates: Partial<Contact>): Promise<Contact> => {
    return fetchWithAuth(`/contact/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  },
};

export const CountriesAPI = {
  getAll: async (search?: string): Promise<Country[]> => {
    const searchParam = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await fetchWithAuth(`/countries${searchParam}`);
    // Ensure we always return an array
    return Array.isArray(response) ? response : [];
  },
};

export const DashboardAPI = {
  getStats: async (): Promise<DashboardStats> => {
    return fetchWithAuth("/dashboard");
  },
};

export const FeaturedAPI = {
  getAll: async (section?: FeaturedSection): Promise<FeaturedCitizen[]> => {
    const sectionParam = section ? `?section=${section}` : "";
    return fetchWithAuth(`/featured${sectionParam}`);
  },

  add: async (
    citizenId: number,
    section: FeaturedSection
  ): Promise<FeaturedCitizen> => {
    return fetchWithAuth("/featured", {
      method: "POST",
      body: JSON.stringify({ citizen_id: citizenId, section }),
    });
  },

  remove: async (id: number): Promise<void> => {
    return fetchWithAuth(`/featured/${id}`, {
      method: "DELETE",
    });
  },

  updateOrder: async (
    section: FeaturedSection,
    items: { citizen_id: number; order: number }[]
  ): Promise<void> => {
    try {
      const payload = {
        section,
        items,
      };

      return await fetchWithAuth("/featured/order", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      throw error;
    }
  },
};

export const GendersAPI = {
  getAll: async (): Promise<Gender[]> => {
    const response = await fetchWithAuth("/genders");
    return response.data || [];
  },
};
